import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ArrowRight, Lock, CalendarBlank } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import SEOHead from "@/lib/seo/SEOHead";

const PRIMARY = "#2D5BFF";
const ACCENT = "#65CE95";
const DARK = "#0F1311";

const VIDEOS = {
  "1": {
    title: "Why Your Local Business Isn't Ranking",
    subtitle: "Step 1 of 3 · ~4 minutes",
    src: "https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/36cew7xu_Ranktrust%20vid%201_1080p%20%281%29.mp4",
    nextLabel: "Continue to Step 2",
    nextPath: "/presell/video-2",
  },
  "2": {
    title: "The 3 Signals Google Actually Rewards",
    subtitle: "Step 2 of 3 · ~4 minutes",
    src: "https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/wlwtnifz_Ranktrust%20vid%202_1080p.mp4",
    nextLabel: "Continue to Step 3",
    nextPath: "/presell/video-3",
  },
  "3": {
    title: "How RankTrust Closes the Gap",
    subtitle: "Step 3 of 3 · ~4 minutes",
    src: "https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/q7g53txw_Ranktrust%20vid%203_1080p.mp4",
    nextLabel: "Book My Demo",
    nextPath: "/register",
    isFinal: true,
  },
};

const Logo = ({ height = 36 }) => (
  <div style={{ display: "inline-block" }}>
    <img
      src="https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/mlg09j9p_Ranktrust%20new%20logo.png"
      alt="RankTrust"
      style={{ height, width: "auto", display: "block" }}
    />
    <div style={{ fontSize: 10, letterSpacing: "1.5px", color: "#4B5563", fontWeight: 700, textTransform: "uppercase", marginTop: 4, whiteSpace: "nowrap" }}>
      The Business Growth Intelligence Platform
    </div>
  </div>
);

export default function Presell() {
  const { pathname } = useLocation();
  const stepMatch = pathname.match(/video-(\d)/);
  const step = stepMatch ? stepMatch[1] : "1";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const lastTrackedPctRef = useRef(0);

  const config = VIDEOS[step];
  const cid = searchParams.get("cid") || "";
  const email = searchParams.get("email") || "";

  const [watchedPct, setWatchedPct] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);

  // The "Next/Book" CTA is unlocked when:
  //  - Steps 1 & 2: as soon as the video ends OR ≥85% watched
  //  - Step 3 (final): hard-gate at 70% before "Book My Demo" appears
  const unlockThreshold = config?.isFinal ? 70 : 85;
  const unlocked = reachedEnd || watchedPct >= unlockThreshold;

  // Reset state when changing steps
  useEffect(() => {
    setWatchedPct(0);
    setReachedEnd(false);
    lastTrackedPctRef.current = 0;
    window.scrollTo(0, 0);
  }, [step]);

  // Send watch progress to the backend every ~10% so we can score lead heat
  const trackProgress = async (pct, completed = false) => {
    if (!cid) return;
    try {
      await axios.post(`${API}/public/presell/track`, {
        check_id: cid,
        email,
        video_step: Number(step),
        watched_pct: pct,
        completed,
      });
    } catch {
      /* swallow — tracking failures must never block the funnel */
    }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
    setWatchedPct(pct);
    // Throttle backend writes to every ~10% increment
    if (pct - lastTrackedPctRef.current >= 10) {
      lastTrackedPctRef.current = pct;
      trackProgress(pct);
    }
  };

  const onEnded = () => {
    setReachedEnd(true);
    setWatchedPct(100);
    trackProgress(100, true);
  };

  const goNext = () => {
    if (!unlocked) return;
    const qs = new URLSearchParams({ cid, email }).toString();
    if (config.isFinal) {
      navigate(`${config.nextPath}?${qs}`);
    } else {
      navigate(`${config.nextPath}?${qs}`);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-black">Step not found</h1>
          <Link to="/check" className="mt-4 inline-block text-sm font-bold" style={{ color: PRIMARY }}>← Back to Growth Assessment</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" style={{ fontFamily: "Manrope, system-ui, sans-serif" }} data-testid={`presell-step-${step}`}>
      <SEOHead
        title={`RankTrust Growth Series — Video ${step}`}
        description="RankTrust Growth Series video for prospects following up on a Business Growth Assessment."
        robots="noindex,follow"
        indexable={false}
      />
      {/* Header */}
      <header className="border-b border-zinc-800/80">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/">
            <Logo height={36} />
          </Link>
          <div className="hidden sm:flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-zinc-400">
            <span className={step === "1" ? "text-white" : ""}>1</span>
            <span className="w-6 h-px bg-zinc-700"></span>
            <span className={step === "2" ? "text-white" : ""}>2</span>
            <span className="w-6 h-px bg-zinc-700"></span>
            <span className={step === "3" ? "text-white" : ""}>3</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Step intro */}
        <div className="mb-6 lg:mb-8">
          <div className="text-xs font-bold tracking-[0.2em] uppercase mb-2" style={{ color: ACCENT }} data-testid="presell-subtitle">
            {config.subtitle}
          </div>
          <h1 className="font-black tracking-tight leading-[1.05] text-3xl lg:text-5xl" data-testid="presell-title">
            {config.title}
          </h1>
        </div>

        {/* Video container */}
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl border border-zinc-800" data-testid="presell-video-wrap">
          <video
            ref={videoRef}
            src={config.src}
            controls
            controlsList="nodownload noremoteplayback"
            playsInline
            preload="metadata"
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            className="w-full block aspect-video bg-black"
            data-testid="presell-video"
          />
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs font-bold tracking-wider uppercase mb-2">
            <span className="text-zinc-400">Watch progress</span>
            <span className="tabular-nums" style={{ color: unlocked ? ACCENT : "#a1a1aa" }} data-testid="presell-progress-text">
              {watchedPct}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${watchedPct}%`,
                background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
              }}
            ></div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col items-start gap-3">
          {unlocked ? (
            <button
              onClick={goNext}
              className="inline-flex items-center gap-2 px-7 py-4 font-bold text-base rounded-lg text-white transition-all hover:-translate-y-0.5 hover:shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }}
              data-testid="presell-cta-unlocked"
            >
              {config.isFinal ? <CalendarBlank size={18} weight="bold" /> : <CheckCircle size={18} weight="bold" />}
              {config.nextLabel}
              <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 px-7 py-4 font-bold text-base rounded-lg cursor-not-allowed border-2 border-zinc-700 text-zinc-500"
              data-testid="presell-cta-locked"
            >
              <Lock size={16} weight="bold" />
              {config.isFinal
                ? `Watch ${unlockThreshold}% to unlock booking`
                : "Finish the video to continue"}
            </button>
          )}
          {config.isFinal && !unlocked && (
            <p className="text-xs text-zinc-500" data-testid="presell-gate-hint">
              Booking unlocks at {unlockThreshold}% watched · currently {watchedPct}%
            </p>
          )}
        </div>
      </main>

      <footer className="py-10 border-t border-zinc-800/80 mt-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-xs text-zinc-500 space-y-3">
          <div data-testid="footer-legal-entity">
            RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} RankTrust · The Business Growth Intelligence Platform</div>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-zinc-300">Terms</Link>
              <Link to="/privacy" className="hover:text-zinc-300">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
